import {
  IonPage,
  IonButtons,
  IonButton,
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
  IonList,
  IonFooter,
} from '@ionic/react';
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';

import { SubTrack, Track } from '@custom-types/Session';

import FilterTrackItem from './FilterTrackItem';

const Footer = styled(IonFooter)`
  padding-bottom: 48px;
`;

type Selected = {
  [id: string]: boolean;
};
type Props = {
  tracks: Track[];
  selected: string[];
  onApply: (items: string[]) => void;
  onDismiss: () => void;
};

/**
 * return percentage of selected items, where 1 means everything was selected and 0 means nothing was selected
 */
export function getSelectedPercentage(selected: Selected, items: SubTrack[] = []): number {
  if (!items.length) {
    return 0;
  }

  const total = items.filter(({ id }) => selected[id]);
  return total.length / items.length;
}

/**
 * toggle state of `id` and update state of `dependency` based on:
 *    - if `dependency` is an array, then we use same state as `id`
 */
export function toggle(
  selected: Selected,
  id: string,
  dependency: SubTrack[] | Track = []
): Selected {
  const next = { ...selected, [id]: !selected[id] };

  if (Array.isArray(dependency)) {
    // case: toggle parent + children
    dependency.forEach(({ id: dependencyId }) => {
      next[dependencyId] = next[id];
    });
  } else {
    // case: toggle child + parent
    next[dependency.id] = !!getSelectedPercentage(next, dependency.subTracks);
  }
  return next;
}

/**
 * return an object with keys as id and state as value after using toggle logic for every id in provided `selected` array.
 */
export function unpack(selected: string[] | undefined, items: Track[]): Selected {
  let unpacked: Selected = {};

  items.forEach(parent => {
    (parent.subTracks || []).forEach(child => {
      if (selected?.includes(child.id)) {
        // child + parent
        unpacked = toggle(unpacked, child.id, parent);
      }
    });

    // parent + children
    if (selected?.includes(parent.id) && !unpacked[parent.id]) {
      unpacked = toggle(unpacked, parent.id, parent.subTracks);
    }
  });

  return unpacked;
}

/**
 * return an array of `selected` id after removing unnecessary information about children if item was totally selected (every child)
 */
export function pack(selected: Selected, items: Track[]): string[] {
  const packed: string[] = [];

  items.forEach(parent => {
    const selectedChildren = getSelectedPercentage(selected, parent.subTracks);

    // parent + children or parent only (when it has no children)
    if (selectedChildren === 1 || (!selectedChildren && selected[parent.id])) {
      packed.push(parent.id);
      return;
    }

    (parent.subTracks || []).forEach(child => {
      // child
      if (selected[child.id]) {
        packed.push(child.id);
      }
    });
  });

  return packed;
}

const FilterTracksModal = ({ tracks, selected: defaultSelected, onApply, onDismiss }: Props) => {
  const [selected, setSelected] = useState<Selected>({});
  const onSelect = useCallback(
    (id: string, dependency: SubTrack[] | Track) =>
      setSelected((prev: Selected) => toggle(prev, id, dependency)),
    []
  );

  const reset = useCallback(() => {
    setSelected(unpack(defaultSelected, tracks));
  }, [tracks, defaultSelected]);

  useEffect(() => {
    reset();
  }, [reset]);

  const handleDismiss = () => {
    onDismiss();
    reset();
  };

  const handleApply = () => {
    onApply(pack(selected, tracks));
    handleDismiss();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t`Filter by Tracks`}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleDismiss}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonList>
          {tracks.map(track => {
            const selectedChildren = getSelectedPercentage(selected, track.subTracks);

            return (
              <Fragment key={track.id}>
                <FilterTrackItem
                  color={track.color}
                  indeterminate={selectedChildren > 0 && selectedChildren < 1}
                  selected={selected[track.id] || selectedChildren > 0}
                  onClick={() => onSelect(track.id, track.subTracks)}
                >
                  {track.name}
                </FilterTrackItem>
                {Array.isArray(track.subTracks) &&
                  track.subTracks.map(subTrack => (
                    <FilterTrackItem
                      key={subTrack.id}
                      indent
                      color={track.color}
                      selected={selected[subTrack.id]}
                      onClick={() => onSelect(subTrack.id, track)}
                    >
                      {subTrack.name}
                    </FilterTrackItem>
                  ))}
              </Fragment>
            );
          })}
        </IonList>
      </IonContent>
      <Footer>
        <IonToolbar>
          <IonButton expand="block" onClick={handleApply}>{t`Apply Filter`}</IonButton>
        </IonToolbar>
      </Footer>
    </IonPage>
  );
};

export default FilterTracksModal;
